import React, { useState, useEffect, useCallback } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolClassSortOrder } from '../services/TimetableService';
import { storeService } from '../services/StoreService';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend, AreaChart, Area, ComposedChart, Line
} from 'recharts';
import './AdminAnalyticsView.css';
import { scopeForAdmin, adminSchoolIds, docInScope } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';

// Normalize a grade doc to a 0-100 percentage. Handles score/maxScore stored as
// numbers or strings, and docs that only carry a precomputed `percentage`.
const gradePercent = (g) => {
  const score = Number(g.score);
  const maxScore = Number(g.maxScore);
  if (Number.isFinite(score) && Number.isFinite(maxScore) && maxScore > 0) {
    return (score / maxScore) * 100;
  }
  const pct = Number(g.percentage);
  return Number.isFinite(pct) ? pct : null;
};

// Attendance `date` is stored as a full ISO8601 string (or Timestamp), not YYYY-MM-DD.
const isSameLocalDay = (v, ref) => {
  if (!v) return false;
  const d = v?.toDate ? v.toDate() : new Date(v);
  if (isNaN(d)) return false;
  return d.getFullYear() === ref.getFullYear() && d.getMonth() === ref.getMonth() && d.getDate() === ref.getDate();
};

function AdminAnalyticsView({ adminData }) {
  const [classes, setClasses] = useState([]);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [parents, setParents] = useState([]);
  const [attendanceRecords, setAttendanceRecords] = useState([]);
  const [allAttendanceRecords, setAllAttendanceRecords] = useState([]);
  const [events, setEvents] = useState([]);
  const [grades, setGrades] = useState([]);
  const [approvals, setApprovals] = useState([]);
  const [reportCards, setReportCards] = useState([]);
  const [teacherPreps, setTeacherPreps] = useState([]);
  const [storeAnalytics, setStoreAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadAnalyticsData = useCallback(async (force = false) => {
    console.log('🔥 AdminAnalytics: Loading data, schoolId:', adminData?.schoolId);
    try {
      const schoolIds = adminSchoolIds(adminData);
      // Cache keys are school-scoped so data never leaks between schools.
      const scope = schoolIds.join('|') || 'all';
      const k = (path) => `${scope}:${path}`;
      if (force) dataCacheService.invalidate(scope);

      // Load classes (load all, then scope case-insensitively)
      const classesData = scopeForAdmin(
        await dataCacheService.getDocs(k('classes'), collection(db, 'classes')),
        adminData
      );
      classesData.sort(schoolClassSortOrder);
      console.log('🔥 AdminAnalytics: Classes loaded', classesData.length);
      setClasses(classesData);

      const classIds = new Set(classesData.map(c => c.id));

      // Per-class subcollections fan out in parallel and are served from the
      // cache on repeat visits — previously each class awaited ~5 sequential
      // getDocs round-trips per mount.
      const [perClass, usersDocs, parentsDocs, eventsDocs, approvalsDocs] = await Promise.all([
        Promise.all(classesData.map(async (cls) => {
          const cid = cls.id;
          const [students, attendance, assignments, classGrades, preps] = await Promise.all([
            dataCacheService.getDocs(k(`classes/${cid}/students`), collection(db, 'classes', cid, 'students')),
            dataCacheService.getDocs(k(`classes/${cid}/attendanceRecords`), collection(db, 'classes', cid, 'attendanceRecords')),
            dataCacheService.getDocs(k(`classes/${cid}/assignments`), collection(db, 'classes', cid, 'assignments')),
            dataCacheService.getDocs(k(`classes/${cid}/grades`), collection(db, 'classes', cid, 'grades')),
            dataCacheService.getDocs(k(`classes/${cid}/teacherPreps`), collection(db, 'classes', cid, 'teacherPreps')),
          ]);
          const [assignmentGrades, studentReportCards] = await Promise.all([
            Promise.all(assignments.map(a =>
              dataCacheService.getDocs(k(`classes/${cid}/assignments/${a.id}/grades`), collection(db, 'classes', cid, 'assignments', a.id, 'grades'))
            )),
            Promise.all(students.map(s =>
              dataCacheService.getDocs(k(`classes/${cid}/students/${s.id}/reportCards`), collection(db, 'classes', cid, 'students', s.id, 'reportCards'))
            )),
          ]);
          return { cid, students, attendance, assignments, classGrades, preps, assignmentGrades, studentReportCards };
        })),
        dataCacheService.getDocs(k('users'), collection(db, 'users')),
        dataCacheService.getDocs(k('parents'), collection(db, 'parents')),
        dataCacheService.getDocs(k('events'), collection(db, 'events')),
        dataCacheService.getDocs(k('gradeChangeRequests'), collection(db, 'gradeChangeRequests')),
      ]);

      // Load students from each class (filter inactive client-side —
      // some docs may not carry an isActive flag)
      const studentsData = [];
      perClass.forEach(({ cid, students: classStudents }) => classStudents.forEach(data => {
        if (data.isActive === false) return;
        studentsData.push({ ...data, classId: data.classId || cid });
      }));
      console.log('🔥 AdminAnalytics: Students loaded', studentsData.length);
      setStudents(studentsData);
      const studentIds = new Set(studentsData.flatMap(s => [s.id, s.studentId].filter(Boolean)));

      // Load teachers
      setTeachers(scopeForAdmin(usersDocs, adminData)
        .filter(u => (u.role || '').toLowerCase() === 'teacher'));

      // Load parents
      setParents(scopeForAdmin(parentsDocs, adminData));

      // Load attendance — `date` is stored as a full ISO8601 string, so filter to
      // today client-side instead of a where('date', '==', ...) equality query.
      const today = new Date();
      const allRecords = [];
      const todayRecords = [];
      perClass.forEach(({ cid, attendance }) => attendance.forEach(data => {
        const rec = { ...data, classId: data.classId || cid };
        allRecords.push(rec);
        if (isSameLocalDay(data.date, today)) todayRecords.push(rec);
      }));
      console.log('🔥 AdminAnalytics: Attendance total', allRecords.length, '| today', todayRecords.length);
      setAllAttendanceRecords(allRecords);
      setAttendanceRecords(todayRecords);

      // Load events (scoped to this school)
      setEvents(scopeForAdmin(eventsDocs, adminData));

      // Load grades from all three places they can live:
      //   classes/{cid}/assignments/{aid}/grades  (primary — iOS + web teacher grading)
      //   classes/{cid}/grades                    (legacy/class-level collection)
      //   grades                                  (top-level — iOS batch writes)
      const gradesByKey = new Map();
      const assignmentToClass = {};
      const pushGrade = (docId, data, fallbackClassId) => {
        const key = `${data.studentId || ''}|${data.assignmentId || docId}`;
        if (gradesByKey.has(key)) return;
        gradesByKey.set(key, { ...data, id: docId, classId: data.classId || fallbackClassId || null });
      };

      perClass.forEach(({ cid, assignments, assignmentGrades, classGrades }) => {
        assignments.forEach((a, i) => {
          assignmentToClass[a.id] = cid;
          assignmentGrades[i].forEach(g => pushGrade(g.id, g, cid));
        });
        // Legacy class-level grades collection
        classGrades.forEach(g => pushGrade(g.id, g, cid));
      });

      // Top-level grades (iOS batch writes have no classId — resolve via assignment/student)
      try {
        const topGradesDocs = await dataCacheService.getDocs(k('grades'), collection(db, 'grades'));
        topGradesDocs.forEach(data => {
          const inScope = docInScope(data.schoolId, schoolIds)
            || classIds.has(assignmentToClass[data.assignmentId])
            || studentIds.has(data.studentId);
          if (inScope) pushGrade(data.id, data, assignmentToClass[data.assignmentId]);
        });
      } catch (e) {
        console.error('🔥 AdminAnalytics: top-level grades query failed (non-fatal):', e);
      }

      const gradesData = [...gradesByKey.values()];
      console.log('🔥 AdminAnalytics: Grades loaded', gradesData.length);
      setGrades(gradesData);

      // Load grade change requests (approvals) — scoped to this school
      setApprovals(scopeForAdmin(approvalsDocs, adminData));

      // Load report cards
      const reportCardsData = [];
      perClass.forEach(({ studentReportCards }) =>
        studentReportCards.forEach(cards => cards.forEach(c => reportCardsData.push(c)))
      );
      console.log('🔥 AdminAnalytics: Report cards loaded', reportCardsData.length);
      setReportCards(reportCardsData);

      // Load teacher preps — real path is classes/{cid}/teacherPreps (per class),
      // not a top-level collection. Include docs without an isActive flag.
      const prepsData = [];
      perClass.forEach(({ cid, preps }) => preps.forEach(data => {
        if (data.isActive === false) return;
        prepsData.push({ ...data, classId: data.classId || cid });
      }));
      console.log('🔥 AdminAnalytics: Teacher preps loaded', prepsData.length);
      setTeacherPreps(prepsData);

      // Load store analytics (scoped to this school)
      try {
        const storeData = await dataCacheService.resolve(k('storeAnalytics'),
          () => storeService.getStoreAnalytics(adminData?.schoolId || null));
        setStoreAnalytics(storeData);
      } catch (e) {
        console.error('Error loading store analytics:', e);
      }
    } catch (error) {
      console.error('Error loading analytics data:', error);
    } finally {
      setLoading(false);
    }
  }, [adminData]);

  useEffect(() => {
    loadAnalyticsData();
  }, [loadAnalyticsData]);

  // ===== Computed metrics =====

  // Attendance statuses are stored capitalized in Firestore ("Present", "Absent",
  // "Late", "Excused", "Unmarked") — compare case-insensitively.
  const statusIs = (r, s) => (r.status || '').toLowerCase() === s;

  const attendanceRate = allAttendanceRecords.length > 0
    ? (allAttendanceRecords.filter(r => statusIs(r, 'present') || statusIs(r, 'excused')).length / allAttendanceRecords.length * 100).toFixed(1)
    : '0.0';

  // ==== iOS-parity computed data (all attendance, not just today) ====

  // Attendance distribution across all records — iOS "Attendance Distribution" donut
  const attendanceDistributionData = [
    { name: 'Present', value: allAttendanceRecords.filter(r => statusIs(r, 'present')).length, color: '#22c55e' },
    { name: 'Absent', value: allAttendanceRecords.filter(r => statusIs(r, 'absent')).length, color: '#ef4444' },
    { name: 'Late', value: allAttendanceRecords.filter(r => statusIs(r, 'late')).length, color: '#f59e0b' },
    { name: 'Excused', value: allAttendanceRecords.filter(r => statusIs(r, 'excused')).length, color: '#14532d' },
  ].filter(d => d.value > 0);
  const attendanceTotal = attendanceDistributionData.reduce((a, d) => a + d.value, 0);

  // Daily attendance rate trend — iOS "Attendance Trend Over Time" (bar + line)
  const attendanceTrend = (() => {
    if (!allAttendanceRecords.length) return [];
    const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const byDay = new Map();
    allAttendanceRecords.forEach(r => {
      const d = r.date?.toDate ? r.date.toDate() : new Date(r.date);
      if (isNaN(d)) return;
      const key = dayKey(d);
      const e = byDay.get(key) || { total: 0, present: 0 };
      e.total++;
      if (statusIs(r, 'present') || statusIs(r, 'excused')) e.present++;
      byDay.set(key, e);
    });
    const keys = [...byDay.keys()].sort();
    if (!keys.length) return [];
    const start = new Date(keys[0] + 'T00:00:00');
    const end = new Date(keys[keys.length - 1] + 'T00:00:00');
    const out = [];
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const key = dayKey(d);
      const e = byDay.get(key);
      out.push({
        date: key,
        label: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        rate: e ? Math.round((e.present / e.total) * 100) : 0,
        present: e ? e.present : 0,
        absent: e ? e.total - e.present : 0,
      });
    }
    return out;
  })();
  const trendRates = attendanceTrend.map(t => t.rate);
  const trendAvg = trendRates.length ? Math.round(trendRates.reduce((a, b) => a + b, 0) / trendRates.length) : 0;
  const trendMax = trendRates.length ? Math.max(...trendRates) : 0;
  const trendMin = trendRates.length ? Math.min(...trendRates) : 0;

  // Per-class attendance rate — iOS "Class Attendance Rate" + "Attendance Breakdown by Class"
  const classAttendance = classes.map(cls => {
    const recs = allAttendanceRecords.filter(r => r.classId === cls.id);
    const present = recs.filter(r => statusIs(r, 'present')).length;
    const absent = recs.filter(r => statusIs(r, 'absent')).length;
    const late = recs.filter(r => statusIs(r, 'late')).length;
    const excused = recs.filter(r => statusIs(r, 'excused')).length;
    return {
      name: cls.name,
      rate: recs.length ? Math.round(((present + excused) / recs.length) * 100) : 0,
      present, absent, late, excused,
      total: recs.length,
    };
  }).filter(c => c.total > 0);
  const classAttendanceSorted = [...classAttendance].sort((a, b) => b.rate - a.rate);
  const top5Classes = classAttendanceSorted.slice(0, 5);
  const bottom5Classes = [...classAttendanceSorted].reverse().slice(0, 5);

  // Event participation — iOS "Event Participation"
  const eventParticipation = events.map(ev => {
    const recs = allAttendanceRecords.filter(r => r.eventId === ev.id);
    return { name: ev.name || 'Event', present: recs.filter(r => statusIs(r, 'present') || statusIs(r, 'excused')).length, total: recs.length };
  }).filter(e => e.total > 0);

  const attendanceData = [
    { name: 'Present', value: attendanceRecords.filter(r => statusIs(r, 'present')).length, color: '#22c55e' },
    { name: 'Absent', value: attendanceRecords.filter(r => statusIs(r, 'absent')).length, color: '#ef4444' },
    { name: 'Late', value: attendanceRecords.filter(r => statusIs(r, 'late')).length, color: '#f59e0b' },
    { name: 'Excused', value: attendanceRecords.filter(r => statusIs(r, 'excused')).length, color: '#22c55e' },
  ].filter(d => d.value > 0);

  const enrollmentByClass = classes.map(cls => ({
    name: cls.name,
    students: students.filter(s => s.classId === cls.id).length,
  })).filter(c => c.students > 0).sort((a, b) => b.students - a.students);

  // Grade distribution
  const gradeDistribution = [
    { name: 'A (90+)', value: 0, color: '#22c55e' },
    { name: 'B (80-89)', value: 0, color: '#22c55e' },
    { name: 'C (70-79)', value: 0, color: '#f59e0b' },
    { name: 'D (60-69)', value: 0, color: '#f97316' },
    { name: 'F (<60)', value: 0, color: '#ef4444' },
  ];
  grades.forEach(g => {
    const pct = gradePercent(g);
    if (pct === null) return;
    if (pct >= 90) gradeDistribution[0].value++;
    else if (pct >= 80) gradeDistribution[1].value++;
    else if (pct >= 70) gradeDistribution[2].value++;
    else if (pct >= 60) gradeDistribution[3].value++;
    else gradeDistribution[4].value++;
  });

  // Class average comparison
  const classAverages = classes.map(cls => {
    const pcts = grades.filter(g => g.classId === cls.id).map(gradePercent).filter(p => p !== null);
    if (pcts.length === 0) return null;
    const avg = pcts.reduce((a, b) => a + b, 0) / pcts.length;
    return { name: cls.name, average: parseFloat(avg.toFixed(1)) };
  }).filter(Boolean).sort((a, b) => b.average - a.average);

  // Teacher workload (classes per teacher)
  const teacherWorkload = teachers.map(t => {
    const teacherClasses = classes.filter(c => c.teacherId === t.id || c.teacherId === t.uid);
    return {
      name: `${t.firstName || ''} ${t.lastName || ''}`.trim() || t.username || 'Unknown',
      classes: teacherClasses.length,
      students: students.filter(s => teacherClasses.some(tc => tc.id === s.classId)).length,
    };
  }).filter(t => t.classes > 0).sort((a, b) => b.students - a.students);

  // Approvals status
  const approvalStatus = (a) => (a.status || 'pending').toLowerCase();
  const approvalsData = [
    { name: 'Pending', value: approvals.filter(a => approvalStatus(a) === 'pending').length, color: '#f59e0b' },
    { name: 'Approved', value: approvals.filter(a => approvalStatus(a) === 'approved').length, color: '#22c55e' },
    { name: 'Rejected', value: approvals.filter(a => approvalStatus(a) === 'rejected').length, color: '#ef4444' },
  ].filter(d => d.value > 0);

  // Report cards by term
  const reportCardsByTerm = {};
  reportCards.forEach(rc => {
    const term = rc.term || 'Unspecified';
    reportCardsByTerm[term] = (reportCardsByTerm[term] || 0) + 1;
  });
  const reportCardsData = Object.entries(reportCardsByTerm).map(([term, count]) => ({ term, count }));

  // Teacher prep status
  const prepStatus = (p) => (p.status || 'draft').toLowerCase();
  const prepStatusData = [
    { name: 'Draft', value: teacherPreps.filter(p => prepStatus(p) === 'draft').length, color: '#9ca3af' },
    { name: 'Submitted', value: teacherPreps.filter(p => prepStatus(p) === 'submitted').length, color: '#22c55e' },
    { name: 'Approved', value: teacherPreps.filter(p => prepStatus(p) === 'approved').length, color: '#22c55e' },
    { name: 'Revision', value: teacherPreps.filter(p => prepStatus(p) === 'revision').length, color: '#f97316' },
  ].filter(d => d.value > 0);

  // Store revenue
  const storeRevenueData = storeAnalytics
    ? Object.entries(storeAnalytics.monthlyRevenue).map(([month, revenue]) => ({ month, revenue }))
    : [];

  // Parent connectivity
  const parentConnectivityData = [
    { name: 'Linked', value: parents.filter(p => p.linkedChildrenIds && p.linkedChildrenIds.length > 0).length, color: '#22c55e' },
    { name: 'Unlinked', value: parents.filter(p => !p.linkedChildrenIds || p.linkedChildrenIds.length === 0).length, color: '#ef4444' },
  ].filter(d => d.value > 0);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading analytics...</p>
      </div>
    );
  }

  return (
    <div className="analytics-dashboard">
      <div className="analytics-header">
        <h2>School Analytics</h2>
        <button
          className="analytics-refresh-btn"
          onClick={() => { setLoading(true); loadAnalyticsData(true); }}
        >
          ↻ Refresh
        </button>
      </div>

      {/* Compact stat cards row — small, all in one row */}
      <div className="analytics-stats-row">
        <div className="mini-stat"><span className="mini-stat-icon">🏫</span><span className="mini-stat-value">{classes.length}</span><span className="mini-stat-label">Classes</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">👥</span><span className="mini-stat-value">{students.length}</span><span className="mini-stat-label">Students</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">👨‍🏫</span><span className="mini-stat-value">{teachers.length}</span><span className="mini-stat-label">Teachers</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">👨‍👩‍👧</span><span className="mini-stat-value">{parents.length}</span><span className="mini-stat-label">Parents</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">✅</span><span className="mini-stat-value">{attendanceRate}%</span><span className="mini-stat-label">Attendance</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">📝</span><span className="mini-stat-value">{grades.length}</span><span className="mini-stat-label">Grades</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">⏳</span><span className="mini-stat-value">{approvals.filter(a => approvalStatus(a) === 'pending').length}</span><span className="mini-stat-label">Pending Approvals</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">📄</span><span className="mini-stat-value">{reportCards.length}</span><span className="mini-stat-label">Report Cards</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">📋</span><span className="mini-stat-value">{teacherPreps.length}</span><span className="mini-stat-label">Teacher Preps</span></div>
        <div className="mini-stat"><span className="mini-stat-icon">📅</span><span className="mini-stat-value">{events.length}</span><span className="mini-stat-label">Events</span></div>
      </div>

      {/* iOS-parity charts — full width, matching the iOS admin analytics */}
      <div className="analytics-charts-grid">

        {/* Attendance Distribution — donut with center total (iOS) */}
        <div className="analytics-chart-card wide">
          <h3>Attendance Distribution</h3>
          {attendanceDistributionData.length > 0 ? (
            <div className="analytics-donut-row">
              <ResponsiveContainer width="50%" height={260}>
                <PieChart>
                  <Pie data={attendanceDistributionData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={75} outerRadius={110} paddingAngle={3}>
                    {attendanceDistributionData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <div className="analytics-donut-legend">
                <div className="analytics-donut-total">{attendanceTotal}<span>Total</span></div>
                {attendanceDistributionData.map(d => (
                  <div key={d.name} className="analytics-legend-row">
                    <span className="analytics-legend-dot" style={{ background: d.color }} />
                    <span className="analytics-legend-name">{d.name}</span>
                    <span className="analytics-legend-count">{d.value}</span>
                    <span className="analytics-legend-pct">({attendanceTotal ? Math.round(d.value / attendanceTotal * 100) : 0}%)</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <EmptyChart icon="📅" message="No attendance data for this period" />}
        </div>

        {/* Attendance Trend Over Time — bars + line (iOS) */}
        <div className="analytics-chart-card wide">
          <h3>Attendance Trend Over Time</h3>
          {attendanceTrend.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={attendanceTrend} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Bar dataKey="rate" fill="#16a34a" fillOpacity={0.35} radius={[4, 4, 0, 0]} name="Rate %" />
                  <Line type="monotone" dataKey="rate" stroke="#16a34a" strokeWidth={3} dot={{ r: 3, fill: '#16a34a' }} name="Rate %" />
                </ComposedChart>
              </ResponsiveContainer>
              <div className="analytics-trend-stats">
                <span><strong style={{ color: '#16a34a' }}>{trendAvg}%</strong> Average</span>
                <span><strong style={{ color: '#22c55e' }}>{trendMax}%</strong> Highest</span>
                <span><strong style={{ color: '#f59e0b' }}>{trendMin}%</strong> Lowest</span>
              </div>
            </>
          ) : <EmptyChart icon="📈" message="No trend data for this period" />}
        </div>

        {/* Class Attendance Rate — vertical bars colored by rate (iOS) */}
        <div className="analytics-chart-card wide">
          <h3>Class Attendance Rate</h3>
          {classAttendanceSorted.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={classAttendanceSorted} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-25} textAnchor="end" height={55} interval={0} tick={{ fontSize: 11 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="rate" radius={[6, 6, 0, 0]} name="Rate %">
                  {classAttendanceSorted.map((c, i) => (
                    <Cell key={i} fill={c.rate >= 90 ? '#22c55e' : c.rate >= 75 ? '#f59e0b' : '#ef4444'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="🏫" message="No class data available" />}
        </div>

        {/* Attendance Breakdown by Class — stacked bars (iOS) */}
        <div className="analytics-chart-card wide">
          <h3>Attendance Breakdown by Class</h3>
          {classAttendance.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={classAttendance} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" angle={-25} textAnchor="end" height={55} interval={0} tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="present" stackId="a" fill="#22c55e" name="Present" />
                  <Bar dataKey="absent" stackId="a" fill="#ef4444" name="Absent" />
                  <Bar dataKey="late" stackId="a" fill="#f59e0b" name="Late" />
                </BarChart>
              </ResponsiveContainer>
            </>
          ) : <EmptyChart icon="📊" message="No class data available" />}
        </div>

        {/* Event Participation — horizontal bars (iOS) */}
        <div className="analytics-chart-card wide">
          <h3>Event Participation</h3>
          {eventParticipation.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(eventParticipation.length * 44, 120)}>
              <BarChart data={eventParticipation} layout="vertical" margin={{ top: 10, right: 40, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={140} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey="present" fill="#22c55e" radius={[0, 6, 6, 0]} name="Present" label={{ position: 'right', fontSize: 12 }} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📅" message="No event data available" />}
        </div>

        {/* Top & Bottom Classes — horizontal bars (iOS) */}
        <div className="analytics-chart-card wide">
          <h3>Top Performing Classes</h3>
          {top5Classes.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(top5Classes.length * 44, 120)}>
              <BarChart data={top5Classes} layout="vertical" margin={{ top: 10, right: 40, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="rate" fill="#22c55e" radius={[0, 6, 6, 0]} name="Rate %" label={{ position: 'right', fontSize: 12, formatter: (v) => `${v}%` }} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="🏆" message="No data available" />}

          <h3 className="analytics-subhead">Lowest Performing Classes</h3>
          {bottom5Classes.length > 0 ? (
            <ResponsiveContainer width="100%" height={Math.max(bottom5Classes.length * 44, 120)}>
              <BarChart data={bottom5Classes} layout="vertical" margin={{ top: 10, right: 40, left: 10, bottom: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 11 }} />
                <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="rate" fill="#ef4444" radius={[0, 6, 6, 0]} name="Rate %" label={{ position: 'right', fontSize: 12, formatter: (v) => `${v}%` }} />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📉" message="No data available" />}
        </div>

        {/* Grade Distribution */}
        <div className="analytics-chart-card">
          <h3>Grade Distribution</h3>
          {gradeDistribution.some(d => d.value > 0) ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={gradeDistribution.filter(d => d.value > 0)} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                  {gradeDistribution.filter(d => d.value > 0).map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📊" message="No grades recorded" />}
        </div>

        {/* Class Averages */}
        <div className="analytics-chart-card">
          <h3>Class Average Scores</h3>
          {classAverages.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={classAverages} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} interval={0} tick={{ fontSize: 10 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="average" fill="#22c55e" radius={[4, 4, 0, 0]} name="Average %" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📈" message="No class averages" />}
        </div>

        {/* Attendance Breakdown */}
        <div className="analytics-chart-card">
          <h3>Attendance Today</h3>
          {attendanceData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={attendanceData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70}>
                  {attendanceData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📅" message="No attendance today" />}
        </div>

        {/* Enrollment by Class */}
        <div className="analytics-chart-card">
          <h3>Enrollment by Class</h3>
          {enrollmentByClass.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={enrollmentByClass} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} interval={0} tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="students" fill="#8b5cf6" radius={[4, 4, 0, 0]} name="Students" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="🏫" message="No enrollment data" />}
        </div>

        {/* Teacher Workload */}
        <div className="analytics-chart-card">
          <h3>Teacher Workload (Students)</h3>
          {teacherWorkload.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={teacherWorkload} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" angle={-30} textAnchor="end" height={60} interval={0} tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="students" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Students" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="👨‍🏫" message="No workload data" />}
        </div>

        {/* Approvals Status */}
        <div className="analytics-chart-card">
          <h3>Grade Change Approvals</h3>
          {approvalsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={approvalsData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                  {approvalsData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="✅" message="No grade change requests" />}
        </div>

        {/* Report Cards by Term */}
        <div className="analytics-chart-card">
          <h3>Report Cards by Term</h3>
          {reportCardsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={reportCardsData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="term" tick={{ fontSize: 10 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                <Tooltip />
                <Bar dataKey="count" fill="#06b6d4" radius={[4, 4, 0, 0]} name="Report Cards" />
              </BarChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📄" message="No report cards" />}
        </div>

        {/* Teacher Prep Status */}
        <div className="analytics-chart-card">
          <h3>Teacher Prep Status</h3>
          {prepStatusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={prepStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70}>
                  {prepStatusData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="📋" message="No teacher preps" />}
        </div>

        {/* Parent Connectivity */}
        <div className="analytics-chart-card">
          <h3>Parent Connectivity</h3>
          {parentConnectivityData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <PieChart>
                <Pie data={parentConnectivityData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={70}>
                  {parentConnectivityData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip />
                <Legend formatter={(value, entry) => `${value} (${entry.payload?.value ?? 0})`} />
              </PieChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="👨‍👩‍👧" message="No parent data" />}
        </div>

        {/* Store Revenue Trend */}
        <div className="analytics-chart-card">
          <h3>Store Revenue Trend</h3>
          {storeRevenueData.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <AreaChart data={storeRevenueData} margin={{ top: 10, right: 20, left: 0, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                <YAxis tickFormatter={(v) => `EC$${v}`} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `EC$${v.toFixed(2)}`} />
                <Area type="monotone" dataKey="revenue" stroke="#22c55e" fill="#22c55e" fillOpacity={0.3} strokeWidth={2} name="Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <EmptyChart icon="🛍️" message="No store revenue" />}
        </div>
      </div>

    </div>
  );
}

function EmptyChart({ icon, message }) {
  return (
    <div className="analytics-chart-empty">
      <span className="analytics-chart-empty-icon">{icon}</span>
      <span>{message}</span>
    </div>
  );
}

export default AdminAnalyticsView;
